import { createStore } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { IS_DEVELOPMENT } from '~/modules/config';
import { i18nStore, selectCurrentLanguage } from '~/modules/stores/i18nStore';
import { wrapKeyword } from '~/modules/utilities/text';
import { getSearchTermSuggestions, Suggestion } from '../services/searchService';
import { TrieService } from '../services/trieService';

const MAX_SEARCH_TERM_SUGGESTIONS = 5;
const INLINE_SUGGESTIONS_STORAGE_KEY = 'inlineSuggestions';

function persistInlineSuggestions(suggestions: Suggestion[]): void {
    try {
        window.sessionStorage.setItem(INLINE_SUGGESTIONS_STORAGE_KEY, JSON.stringify(suggestions));
    } catch {
        /* ignore quota/security errors */
    }
}

/** @internal Exported for unit tests. */
export function loadInlineSuggestions(): Suggestion[] {
    try {
        const raw = window.sessionStorage.getItem(INLINE_SUGGESTIONS_STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

export interface searchTermSuggestionState extends State {
    searchTermSuggestions: Map<string, Suggestion[]>;
    searchTermSuggestionsFromTrie: TrieService;
    inlineSuggestions: Suggestion[];
}

const initialState: searchTermSuggestionState = {
    searchTermSuggestions: new Map<string, Suggestion[]>(),
    searchTermSuggestionsFromTrie: new TrieService(),
    inlineSuggestions: loadInlineSuggestions(),
};

export const selectSearchTermSuggestions = (state: searchTermSuggestionState, keyword: string) => {
    return state.searchTermSuggestions.get(keyword);
};

export const selectSearchTermSuggestionsFromTrie = async (
    state: searchTermSuggestionState,
    keyword: string,
): Promise<Suggestion[]> => {
    if (!state.searchTermSuggestionsFromTrie.isInitialized) {
        await state.searchTermSuggestionsFromTrie.initialize();
    }
    return state.searchTermSuggestionsFromTrie.search(keyword, MAX_SEARCH_TERM_SUGGESTIONS).map((item) => ({
        suggestedKeyword: item.keyword,
        highlighted: wrapKeyword(item.keyword, keyword),
    }));
};

export const selectInlineSuggestions = (state: searchTermSuggestionState): Suggestion[] => {
    return state.inlineSuggestions;
};

export const searchTermSuggestionStore = createStore(
    initialState,

    (set: SetState<searchTermSuggestionState>, get: GetState<searchTermSuggestionState>) => ({
        async fetchSearchTermSuggestions(keyword: string) {
            const existingSearchTermSuggestions = selectSearchTermSuggestions(get(), keyword);
            const noPreviousSuggestions = !existingSearchTermSuggestions || existingSearchTermSuggestions.length === 0;
            if (noPreviousSuggestions) {
                const suggestionResult = await getSearchTermSuggestions(keyword);
                // @ts-expect-error * Automated Type Error Fix
                set(function updateSearchResults(draft: searchTermSuggestionState) {
                    draft.searchTermSuggestions.set(keyword, suggestionResult.suggestions);
                });
            }
        },

        setInlineSuggestions(suggestions: Suggestion[]) {
            // @ts-expect-error * Automated Type Error Fix
            set(function updateInlineSuggestions(draft: searchTermSuggestionState) {
                draft.inlineSuggestions = suggestions;
            });
            persistInlineSuggestions(suggestions);
        },

        reset() {
            const state = get();
            state.searchTermSuggestionsFromTrie.reset();
            try {
                window.sessionStorage.removeItem(INLINE_SUGGESTIONS_STORAGE_KEY);
            } catch {
                /* ignore */
            }
            set(function resetStore() {
                return initialState;
            });
        },
    }),
    'searchTermSuggestionStore',
    IS_DEVELOPMENT,
);
function getCurrentLanguage() {
    return selectCurrentLanguage(i18nStore.getState());
}
